import {Injectable} from "@angular/core";
import {BehaviorSubject, finalize, Observable, Subject} from "rxjs";
import {
  CustomerInteractionTypeInternalEnum,
  InteractionDocumentDto,
  UserInteractionDto,
  UserInteractionTypeDto
} from "../types/ds-interaction.types";
import {ActivatedRoute, Router} from "@angular/router";
import {InteractionApiService} from "../api/interaction-api.service";
import {UploadDocumentType} from "../../core/component/core-upload/core-upload.component";
import {tap} from "rxjs/operators";
import {FormDto, FormFieldDto} from "../../core/interfaces/core.types";
import {AppMessageService} from "../../core/service/app-message.service";
import {MessageHelper} from "../../core/helper/message-helper";

export type InteractionServiceStep = "intro" | "form" | "schedule" | "final" | undefined;
export type InteractionEvent = "next" | "previous" | "finalize" | null;
export type InteractionSubjectData = {
  step?: InteractionServiceStep,
  type?: UserInteractionTypeDto,
  interaction?: UserInteractionDto
};
export type InteractionButtons = { next: boolean, previous: boolean, finalize: boolean, overview: boolean };

@Injectable({
  providedIn: 'root'
})
export class InteractionService {

  private _interactionSubject = new BehaviorSubject<InteractionSubjectData>({});
  private _eventSubject = new Subject<InteractionEvent>();
  private _buttonsSubject = new BehaviorSubject<InteractionButtons>({
    next: false,
    previous: false,
    finalize: false,
    overview: false
  });
  private _busySubject = new BehaviorSubject<boolean>(false);


  private _nextStepVetoHandler: ((newValue: any, currentValue: any) => boolean)[] = [];

  constructor(
    protected route: ActivatedRoute,
    protected interactionApiService: InteractionApiService,
    protected messageService: AppMessageService,
    protected router: Router) {
  }

  initialize() {
    this._busySubject.next(false);
    this._interactionSubject.next({});
  }

  get interactionSubject(): BehaviorSubject<InteractionSubjectData> {
    return this._interactionSubject;
  }

  get eventSubject(): Subject<InteractionEvent> {
    return this._eventSubject;
  }

  get buttonsSubject(): BehaviorSubject<InteractionButtons> {
    return this._buttonsSubject;
  }

  get busySubject(): BehaviorSubject<boolean> {
    return this._busySubject;
  }

  get nextStepVetoHandler(): ((newValue: any, currentValue: any) => boolean)[] {
    return this._nextStepVetoHandler;
  }

  /**
   * Create Document.
   * @param interactionId
   * @param files
   * @param callback
   */
  public uploadDocuments(files: UploadDocumentType[], formValues: any) {

    if (files.length == 0) {
      return;
    }

    let interactionSubjectData = this._interactionSubject.getValue();

    if (!interactionSubjectData || !interactionSubjectData.interaction || !interactionSubjectData.interaction.id) {

      this._busySubject.next(true);

      let formDto = this.convertFormValuesToForm(formValues);

      // we first need to create an interaction
      this.createInteraction(formDto as FormDto, false).subscribe(value => {
        this.uploadDocumentInternal(value.id, files);
      }, error => {
        MessageHelper.showErrorMessage("Fehler bei der Anlage des Services aufgetreten!", this.messageService);
        this._busySubject.next(false);
      });

    } else {
      this.uploadDocumentInternal(interactionSubjectData.interaction.id, files);
    }

  }

  protected uploadDocumentInternal(interactionId: number, files: UploadDocumentType[]) {

    let count = 0

    this._busySubject.next(true);

    for (let file of files) {

      const document: InteractionDocumentDto = {
        interactionId: interactionId,
        name: file.name,
        fileBase64: file.fileBase64,
        fileBase64MimeType: file.fileBase64MimeType
      };

      this.interactionApiService.createDocument(interactionId, document).subscribe(res => {
        count++;
        if (files.length == count) {

          this.refreshInteraction().pipe(
            finalize(() => this.busySubject.next((false)))
          ).subscribe(value => {
          });

        }
      }, error => {
        // we need to release the busy state already now
        this._busySubject.next(false);
      });

    }

  }

  /**
   * Delete Document.
   * @param documentId
   */
  deleteDocument(documentId: number) {

    let interactionSubjectData = this._interactionSubject.getValue();

    if (interactionSubjectData.interaction && interactionSubjectData.interaction.id) {

      this._busySubject.next(true);

      this.interactionApiService.deleteInteractionTypeDocument(interactionSubjectData.interaction.id, documentId).pipe(
        finalize(() => {

          // refresh interaction
          this.refreshInteraction().pipe(
            finalize(() => this._busySubject.next(false))
          ).subscribe();
        })
      ).subscribe(
        value => {
          // no activity
          this.messageService.showInfoMessage('', 'Dokument wurde gelöscht.');
        }, error => {
          // this.messageService.showErrorMessage("", "Bei der Löschung des Dokumentes kam es zu einem Fehler!");
          MessageHelper.showErrorMessage("Bei der Löschung des Dokumentes kam es zu einem Fehler!", this.messageService);
        });
    } else {
      throw new Error("deleteDocument: Interaction is not set");
    }

  }


  getDocument(documentId: number, blob: string) {

    let interactionSubjectData = this._interactionSubject.getValue();

    const interactionId = interactionSubjectData.interaction?.id;

    if (interactionId && documentId) {
      this.interactionApiService.getDocumentBinary(interactionId, documentId).subscribe(res => {
        blob = res;
      }, error => {
        MessageHelper.showErrorMessage(error.error.data, this.messageService);
      });
    }
  }


  /**
   * Update Form.
   * @param values
   */
  saveForm(formValues: any, nextStep: boolean) {

    if (!formValues) {
      throw new Error("saveForm: No form values were provided!");
    }

    let interactionSubjectData = this._interactionSubject.getValue();

    if (!interactionSubjectData || !interactionSubjectData.interaction || !interactionSubjectData.interaction.id) {

      this._busySubject.next(true);

      let formDto = this.convertFormValuesToForm(formValues);

      // we first need to create an interaction
      this.createInteraction(formDto as FormDto, true).subscribe(value => {
        if (nextStep) {
          this.nextStep();
        }

        this._busySubject.next(false);

      }, error => {
        // this.messageService.showErrorMessage("", "Fehler bei der Anlage des Services aufgetreten!");
        MessageHelper.showErrorMessage("Fehler bei der Anlage des Services aufgetreten!", this.messageService);
        this._busySubject.next(false);
      });

    } else {
      this.safeFormInternal(interactionSubjectData.interaction.id, formValues, nextStep);
    }

  }

  protected convertFormValuesToForm(values: any) {
    const formFields: FormFieldDto[] = Object.keys(values).map((key) => {
      let value: any = values[key];
      // If the value is an object and has a 'key' property, use that; otherwise, use the value directly.
      if (typeof value === 'object' && value !== null && 'key' in value) {
        value = value.key;
      }
      return {id: key, value};
    });

    const formDto: Partial<FormDto> = {formFields: formFields}

    return formDto;
  }

  protected safeFormInternal(interactionId: number, formValues: any, nextStep: boolean) {

    let formDto = this.convertFormValuesToForm(formValues);

    this._busySubject.next(true);

    // update the form
    this.interactionApiService.updateInteractionForm(interactionId, formDto as FormDto).subscribe(res => {

        this.storeInteraction(res);

        this.busySubject.next(false);

        if (nextStep) {
          this.nextStep();
        }

      }, error => {
        MessageHelper.showErrorMessage(error.error.data, this.messageService);
        this.busySubject.next(false);
      }
    );

  }


  /**
   * Create interaction.
   * @param form
   * @param forceUpdate
   */
  createInteraction(form: FormDto, forceUpdate: boolean): Observable<UserInteractionDto> {

    let interactionSubjectData = this._interactionSubject.getValue();

    if (interactionSubjectData.type) {

      return this.interactionApiService.createInteraction(interactionSubjectData.type.id, form, forceUpdate).pipe(
        tap(
          value => {
            // update interaction
            this.storeInteraction(value);
          }
        )
      );

    }

    throw new Error("createInteraction: Type not set!")

  }

  /**
   * Store interaction.
   * @param interaction
   */
  storeInteraction(interaction: UserInteractionDto) {
    let currentData = this._interactionSubject.getValue();

    this._interactionSubject.next({
      type: currentData.type,
      interaction: interaction,
      step: currentData.step
    });
  }

  refreshInteraction(): Observable<UserInteractionDto> {

    let currentData = this._interactionSubject.getValue();

    if (currentData && currentData.interaction) {
      return this.interactionApiService.getInteraction(currentData.interaction.id).pipe(
        tap(value => {
          this.storeInteraction(value);
        })
      );
    }

    throw new Error("refreshInteraction: Not possible as interaction is missing!");
  }

  public finalizeInteractionSoft(scheduleTypeId: number) {
    this.finalizeInteraction(scheduleTypeId, undefined, true);
  }

  public finalizeInteraction(scheduleTypeId: number, freeSlotId?: string, soft?: boolean) {

    const interactionSubjectData = this._interactionSubject.getValue();

    if (!interactionSubjectData.interaction) {
      throw new Error("Interaction is not active!");
    }

    this._busySubject.next(true);

    this.interactionApiService.finalizeInteraction(interactionSubjectData.interaction.id, scheduleTypeId, freeSlotId).subscribe(value => {

      if (!soft) {
        // change to final step
        this._interactionSubject.next({
          type: value.typeDto,
          interaction: value,
          step: "final"
        });
      }


      this._busySubject.next(false);

    }, error => {
      // this.messageService.showErrorMessage("", "Fehler aufgetreten");
      MessageHelper.showErrorMessage("Fehler aufgetreten", this.messageService)
      this._busySubject.next(false);
    });


  }


  raiseEvent(event: InteractionEvent) {
    this._eventSubject.next(event);
  }


  /**
   * Continue interaction. It could be with or without an existing interaction ID.
   * @param interactionTypeId
   * @param interactionId
   */
  public continueInteraction(interactionTypeId: number | "contactUs", step?: InteractionServiceStep, interactionId?: number) {

    const interactionSubjectData = this._interactionSubject.getValue();

    if (interactionId) {

      // check whether the interaction was already retrieved
      if (!interactionSubjectData.interaction || interactionSubjectData.interaction.id != interactionId) {

        this._busySubject.next(true);

        // retrieve interaction, as it was not loaded yet (most likely user-hard-browser-refresh)
        this.interactionApiService.getInteraction(interactionId).subscribe(value => {

            this._interactionSubject.next({
              interaction: value,
              type: value.typeDto,
              step: this.getFirstStep(value.typeDto)
            });
            this.postContinueInteraction();

            this._busySubject.next(false);
          }, error => {
            MessageHelper.showErrorMessage(error.error.data, this.messageService);
            this._busySubject.next(false);
          }
        );

        return;
      }
    } else if (interactionTypeId) {

      if (!interactionSubjectData.type || interactionSubjectData.type.id != interactionTypeId) {

        this._busySubject.next(true);

        // retrieve interaction type, as it was not loaded yet (could be the first load or a hard refresh)
        this.interactionApiService.getInteractionType(interactionTypeId).subscribe(value => {
            this._interactionSubject.next({type: value, step: this.getFirstStep(value)});
            this.postContinueInteraction();

            this._busySubject.next(false);
          }, error => {
            this._busySubject.next(false);
          }
        );

        return;
      }

    } else {
      // this cannot happen, as at least the interaction type needs to be provided, we'll navigate simply
      // back to the overview
      console.error("continueInteraction without interactionType or interaction being passed");
      this.backToOverview();
      return; // stop any further processing
    }

    // finally we need to check whether the step might have changed
    if (interactionSubjectData.step != step) {

      interactionSubjectData.step = step;

      this._interactionSubject.next({
        type: interactionSubjectData.type,
        interaction: interactionSubjectData.interaction,
        step: step
      });
    }

    // no additional data needs to be loaded, so we'll process the post continue interaction
    this.postContinueInteraction();

  }

  protected postContinueInteraction() {
    this.deriveNextAndPreviousButtons();
  }

  /**
   * Calculate the button accesss, which will be used within the interaction component.
   * @protected
   */
  protected deriveNextAndPreviousButtons() {
    const interactionSubjectData = this._interactionSubject.getValue();

    const interactionButtons: InteractionButtons = {
      next: false,
      previous: false,
      overview: false,
      finalize: false
    };


    if (interactionSubjectData.step == "intro") {

      if (interactionSubjectData.type?.hasForm || interactionSubjectData.type?.hasAppointmentSchedule) {
        interactionButtons.next = true;
      }

    } else if (interactionSubjectData.step == "form") {

      if (interactionSubjectData.type?.hasAppointmentSchedule) {
        interactionButtons.next = true;
      } else {
        interactionButtons.finalize = true;
      }

      // no previous step required
      //if (interactionSubjectData.type?.hasInfoSteps) {
      //  interactionButtons.previous = true;
      //}

    } else if (interactionSubjectData.step == "schedule") {

      if (interactionSubjectData.type?.internalType == CustomerInteractionTypeInternalEnum.contact_us) {
        // TICKET: 1000000610
        // interactionButtons.finalize = true;
      }

      // interactionButtons.finalize = true;


      if (interactionSubjectData.type?.hasInfoSteps || interactionSubjectData.type?.hasForm) {
        interactionButtons.previous = true;
      }

    } else if (interactionSubjectData.step == "final") {
      interactionButtons.overview = true;
    }

    this._buttonsSubject.next(interactionButtons);

  }


  backToOverview() {
    this._interactionSubject.next({});
    this.router.navigate(["ci/services"]);
  }


  getFirstStep(interactionType: UserInteractionTypeDto): InteractionServiceStep | undefined {

    if (interactionType.hasInfoSteps) {
      return "intro";
    } else if (interactionType.hasForm) {
      return "form";
    } else if (interactionType.hasAppointmentSchedule) {
      return "schedule";
    }

    return undefined;

  }


  previousStep() {

    const interactionSubjectData = this._interactionSubject.getValue();

    if (!interactionSubjectData.type) {
      throw new Error("nextStep: Interaction Type is missing");
    } else if (!interactionSubjectData.step) {
      throw new Error("nextStep: Current step is missing");
    }

    let previousStep: InteractionServiceStep;

    if (interactionSubjectData.step == "intro") {
      // there is no previous step for intro, so we keep intro
      previousStep = "intro";
    } else if (interactionSubjectData.step == "form") {

      if (interactionSubjectData.type.hasInfoSteps) {
        previousStep = "intro";
      } else {
        // there is no previous step
        previousStep = "form";
      }

    } else if (interactionSubjectData.step == "schedule") {

      if (interactionSubjectData.type.hasForm) {
        previousStep = "form";
      } else if (interactionSubjectData.type.hasInfoSteps) {
        previousStep = "intro";
      } else {
        // there is no previous step
        previousStep = "schedule";
      }

    } else if (interactionSubjectData.step == "final") {

      // we can't go back from the final step
      previousStep = "final";
    }

    this._interactionSubject.next({
      type: interactionSubjectData.type,
      interaction: interactionSubjectData.interaction,
      step: previousStep
    });

  }

  /**
   * Process next step.
   * @param startStep
   */
  nextStep() {

    const interactionSubjectData = this._interactionSubject.getValue();

    if (!interactionSubjectData.type) {
      throw new Error("nextStep: Interaction Type is missing");
    }

    let nextStep: InteractionServiceStep;

    if (!interactionSubjectData.step) {

      nextStep = this.getFirstStep(interactionSubjectData.type);

    } else if (interactionSubjectData.step == "intro") {
      if (interactionSubjectData.type.hasForm) {
        nextStep = "form";
      } else if (interactionSubjectData.type.hasAppointmentSchedule) {
        nextStep = "schedule";
      } else {
        nextStep = "final";
      }
    } else if (interactionSubjectData.step == "form") {
      if (interactionSubjectData.type.hasAppointmentSchedule) {
        nextStep = "schedule";
      } else {
        nextStep = "final";
      }
    } else if (interactionSubjectData.step == "schedule") {
      nextStep = "final";
    } else if (interactionSubjectData.step == "final") {

      console.error("nextStep: The final step was reached and therefor this method must not be called!");
      this.backToOverview();
      return;

    }

    this._interactionSubject.next({
      type: interactionSubjectData.type,
      interaction: interactionSubjectData.interaction,
      step: nextStep
    });

  }

}
